import { useState, useEffect, useRef } from 'preact/hooks'
import { Lock, Lightbulb, CheckCircle, Wand2, Plus, Trash2, Check, X } from 'lucide-preact'
import { Select } from '@/components/common/Select'
import { TextBox } from '@/components/common/TextBox'
import { ToggleRow } from '@/components/common/ToggleRow'
import type { HubSpotMappingConfig, HubSpotProperty, HubSpotFieldMapping } from '@/types/hubspot'
import { LEAD_SOURCE_PROPERTY, LEAD_SOURCE_PROPERTY_LABEL, LEFT_TO_HUBSPOT } from './leadSource'

/**
 * Auto-map dictionary: normalised OrbitalX field key → HubSpot internal property name.
 * Covers standard LinkedIn lead-gen / registration fields plus common enrichment fields.
 * Keys are lowercase so lookup is case-insensitive.
 */
const AUTO_MAP: Record<string, string> = {
    // Identity
    firstname: 'firstname',
    first_name: 'firstname',
    lastname: 'lastname',
    last_name: 'lastname',
    email: 'email',
    emailaddress: 'email',
    email_address: 'email',

    // Contact info
    phone: 'phone',
    phonenumber: 'phone',
    phone_number: 'phone',
    mobilephone: 'mobilephone',
    mobile_phone: 'mobilephone',

    // Company / professional
    company: 'company',
    companyname: 'company',
    company_name: 'company',
    jobtitle: 'jobtitle',
    job_title: 'jobtitle',
    jobfunction: 'job_function',
    job_function: 'job_function',
    industry: 'industry',
    companysize: 'numemployees',
    company_size: 'numemployees',
    numberofemployees: 'numemployees',
    number_of_employees: 'numemployees',

    // Location
    country: 'country',
    countrycode: 'country',
    country_code: 'country',
    city: 'city',
    state: 'state',
    stateregion: 'state',
    state_region: 'state',
    region: 'state',
    postalcode: 'zip',
    postal_code: 'zip',
    zipcode: 'zip',
    zip_code: 'zip',
    zip: 'zip',
    address: 'address',
    streetaddress: 'address',
    street_address: 'address',

    // Web / social
    website: 'website',
    websiteurl: 'website',
    website_url: 'website',
    companywebsite: 'website',
    company_website: 'website',
    linkedinurl: 'hs_linkedinbio',
    linkedin_url: 'hs_linkedinbio',
    linkedinprofile: 'hs_linkedinbio',
    linkedin_profile: 'hs_linkedinbio',
    twitterhandle: 'twitterhandle',
    twitter_handle: 'twitterhandle',

    // Other common
    salutation: 'salutation',
    fax: 'fax',
}

interface FieldMappingStepProps {
    config: HubSpotMappingConfig
    properties: HubSpotProperty[]
    onChange: (config: HubSpotMappingConfig) => void
}

export function FieldMappingStep({ config, properties, onChange }: FieldMappingStepProps) {
    const [showAddRow, setShowAddRow] = useState(false)
    const [newLabel, setNewLabel] = useState('')
    const [newHubSpotProp, setNewHubSpotProp] = useState('')

    const mappedCount = config.FieldMappings.filter(m => m.IsEnabled && m.HubSpotProperty).length

    const updateMapping = (index: number, updates: Partial<HubSpotFieldMapping>) => {
        const updated = [...config.FieldMappings]
        updated[index] = { ...updated[index], ...updates }
        onChange({ ...config, FieldMappings: updated })
    }

    const removeMapping = (index: number) => {
        const updated = config.FieldMappings.filter((_, i) => i !== index)
        onChange({ ...config, FieldMappings: updated })
    }

    // Original Traffic Source and its drill-downs are HubSpot's own and never written, so never offered (ORB-1056).
    const writable = properties.filter(p => !LEFT_TO_HUBSPOT.has(p.Name.toLowerCase()))
    // OrbitalX Lead Source is always known. The sync creates it before its first write, so an account can lack it
    // until a contact has been pushed; without it here the default mapping would read as unmapped, and Auto-map
    // would move it onto a client property labelled "Lead Source".
    const hasLeadSourceProperty = writable.some(p => p.Name.toLowerCase() === LEAD_SOURCE_PROPERTY)
    // Case-insensitive property lookup: lowercase name → actual Name
    const propertyByLower = new Map(writable.map(p => [p.Name.toLowerCase(), p.Name]))
    if (!hasLeadSourceProperty) propertyByLower.set(LEAD_SOURCE_PROPERTY, LEAD_SOURCE_PROPERTY)
    // Label-based lookup: normalized label → actual Name
    const propertyByLabel = new Map(writable.map(p => [p.Label.replace(/[\s-]/g, '').toLowerCase(), p.Name]))
    const propertyOptions = [
        ...writable.map(p => ({ value: p.Name, label: `${p.Label} (${p.Name})` })),
        ...(hasLeadSourceProperty
            ? []
            : [{ value: LEAD_SOURCE_PROPERTY, label: `${LEAD_SOURCE_PROPERTY_LABEL} (${LEAD_SOURCE_PROPERTY}), created on the first sync` }]),
    ]

    // ---- Auto-map logic (3-tier matching) ----
    const handleAutoMap = () => {
        const usedProperties = new Set(
            config.FieldMappings.filter(m => m.HubSpotProperty).map(m => m.HubSpotProperty)
        )

        const updated = config.FieldMappings.map(mapping => {
            if (mapping.IsSystemMapped) return mapping
            // If already mapped, normalize casing to match actual property name
            if (mapping.HubSpotProperty) {
                const actual = propertyByLower.get(mapping.HubSpotProperty.toLowerCase())
                if (actual) {
                    return actual === mapping.HubSpotProperty ? mapping : { ...mapping, HubSpotProperty: actual }
                }
            }

            const fieldKey = mapping.OrbitalXField.replace(/[\s-]/g, '').toLowerCase()
            let resolvedProperty: string | undefined


            // Tier 1: Static dictionary (case-insensitive property lookup)
            const dictSuggestion = AUTO_MAP[fieldKey]
            if (dictSuggestion) {
                resolvedProperty = propertyByLower.get(dictSuggestion.toLowerCase())
            }

            // Tier 2: Match DisplayLabel against HubSpot property Labels
            if (!resolvedProperty && mapping.DisplayLabel) {
                const labelKey = mapping.DisplayLabel.replace(/[\s-]/g, '').toLowerCase()
                resolvedProperty = propertyByLabel.get(labelKey)
            }

            // Tier 3: Match OrbitalXField key against HubSpot property Labels
            if (!resolvedProperty) {
                resolvedProperty = propertyByLabel.get(fieldKey)
            }

            if (resolvedProperty && !usedProperties.has(resolvedProperty)) {
                usedProperties.add(resolvedProperty)
                return { ...mapping, HubSpotProperty: resolvedProperty, IsEnabled: true }
            }
            return mapping
        })

        onChange({ ...config, FieldMappings: updated })
    }

    // Auto-suggest mappings on first render when there are unmapped fields
    const autoMapRan = useRef(false)
    useEffect(() => {
        if (autoMapRan.current || properties.length === 0) return
        const hasUnmapped = config.FieldMappings.some(m => !m.HubSpotProperty && !m.IsSystemMapped)
        if (!hasUnmapped) return
        autoMapRan.current = true
        handleAutoMap()
    }, [properties])

    const unmappedCount = config.FieldMappings.filter(
        m => !m.HubSpotProperty && !m.IsSystemMapped
    ).length

    // ---- Custom field logic ----
    const addCustomField = () => {
        if (!newLabel.trim()) return
        const fieldKey = `Custom_${newLabel.trim().replace(/\s+/g, '_')}`

        // Prevent duplicate keys
        if (config.FieldMappings.some(m => m.OrbitalXField === fieldKey)) return

        const newMapping: HubSpotFieldMapping = {
            OrbitalXField: fieldKey,
            DisplayLabel: newLabel.trim(),
            HubSpotProperty: newHubSpotProp || null,
            IsEnabled: true,
            IsRequired: false,
            IsSystemMapped: false,
            IsCustom: true,
        }

        onChange({ ...config, FieldMappings: [...config.FieldMappings, newMapping] })
        setNewLabel('')
        setNewHubSpotProp('')
        setShowAddRow(false)
    }

    return (
        <div className="space-y-5">
            {/* Header */}
            <div className="flex items-start justify-between">
                <div>
                    <h3 className="text-xl font-semibold text-slate-900 dark:text-white">Map Fields to HubSpot</h3>
                    <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                        Define how OrbitalX data populates your HubSpot contact records.
                    </p>
                </div>
                <div className="flex items-center gap-3">
                    {unmappedCount > 0 && (
                        <button
                            type="button"
                            onClick={handleAutoMap}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border border-primary-200 dark:border-primary-800 bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300 hover:bg-primary-100 dark:hover:bg-primary-900/40 transition-colors"
                        >
                            <Wand2 className="w-3.5 h-3.5" />
                            Auto-Map
                        </button>
                    )}
                    <div className="text-right text-xs text-slate-500 dark:text-slate-400">
                        <span>{config.FieldMappings.length} fields</span>
                        <span className="mx-1.5">|</span>
                        <span className="text-primary-500 font-medium">{mappedCount} mapped</span>
                    </div>
                </div>
            </div>

            {/* Column headers */}
            <div className="grid grid-cols-12 gap-3 px-3 text-[10px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500">
                <div className="col-span-1" />
                <div className="col-span-4">OrbitalX Field</div>
                <div className="col-span-5">HubSpot Property</div>
                <div className="col-span-2" />
            </div>

            {/* Field rows */}
            <div className="space-y-1.5">
                {config.FieldMappings.map((mapping, index) => (
                    <div
                        key={mapping.OrbitalXField}
                        className={`grid grid-cols-12 gap-3 items-center px-3 py-3 rounded-lg border transition-colors ${
                            mapping.IsEnabled
                                ? 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700'
                                : 'bg-slate-50 dark:bg-slate-800/50 border-slate-100 dark:border-slate-800 opacity-60'
                        }`}
                    >
                        {/* Toggle */}
                        <div className="col-span-1 flex justify-center">
                            <ToggleRow
                                checked={mapping.IsEnabled}
                                disabled={mapping.IsRequired}
                                onChange={() => !mapping.IsRequired && updateMapping(index, { IsEnabled: !mapping.IsEnabled })}
                                ariaLabel={`Enable ${mapping.DisplayLabel || mapping.OrbitalXField}`}
                                size="sm"
                            />
                        </div>

                        {/* OrbitalX Field */}
                        <div className="col-span-4">
                            <div className="flex items-center gap-2">
                                <span className="font-medium text-sm text-slate-900 dark:text-white">{mapping.DisplayLabel || mapping.OrbitalXField}</span>
                                {mapping.IsRequired && (
                                    <span className="px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider rounded bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300">
                                        Required
                                    </span>
                                )}
                                {mapping.IsCustom && (
                                    <span className="px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider rounded bg-violet-100 dark:bg-violet-900/30 text-violet-700 dark:text-violet-300">
                                        Custom
                                    </span>
                                )}
                                {mapping.FixedValue && (
                                    <span className="px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider rounded bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300">
                                        Fixed Value
                                    </span>
                                )}
                            </div>
                            {mapping.Description && (
                                <p className="text-[10px] text-slate-400 dark:text-slate-500 mt-0.5">{mapping.Description}</p>
                            )}
                        </div>

                        {/* HubSpot Property Dropdown */}
                        <div className="col-span-5">
                            {mapping.IsSystemMapped ? (
                                <div className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-slate-100 dark:bg-slate-700 text-sm text-slate-600 dark:text-slate-300">
                                    <Lock className="w-3 h-3" />
                                    <span>{properties.find(p => p.Name === mapping.HubSpotProperty)?.Label || mapping.HubSpotProperty} ({mapping.HubSpotProperty})</span>
                                </div>
                            ) : mapping.FixedValue !== undefined && mapping.FixedValue !== null ? (
                                <div className="flex gap-2">
                                    <Select
                                        key={`hubspot-prop-${mapping.OrbitalXField}-${mapping.HubSpotProperty || 'none'}`}
                                        name={`hubspot-prop-${mapping.OrbitalXField}`}
                                        options={propertyOptions}
                                        placeholder="Select HubSpot property..."
                                        defaultValue={mapping.HubSpotProperty || ''}
                                        onChange={(e: Event) => updateMapping(index, { HubSpotProperty: (e.target as HTMLSelectElement).value || null })}
                                        containerClassName="flex-1"
                                    />
                                    <TextBox
                                        name={`fixed-val-${mapping.OrbitalXField}`}
                                        value={mapping.FixedValue || ''}
                                        onChange={(next) => updateMapping(index, { FixedValue: next })}
                                        placeholder="Fixed value"
                                        containerClassName="w-36"
                                    />
                                </div>
                            ) : (
                                <Select
                                    key={`hubspot-prop-${mapping.OrbitalXField}-${mapping.HubSpotProperty || 'none'}`}
                                    name={`hubspot-prop-${mapping.OrbitalXField}`}
                                    options={propertyOptions}
                                    placeholder="Select HubSpot property..."
                                    defaultValue={mapping.HubSpotProperty || ''}
                                    onChange={(e: Event) => updateMapping(index, { HubSpotProperty: (e.target as HTMLSelectElement).value || null })}
                                />
                            )}
                        </div>

                        {/* Status / Actions */}
                        <div className="col-span-2 flex items-center justify-end gap-2">
                            {mapping.IsSystemMapped && mapping.IsEnabled && (
                                <span className="inline-flex items-center gap-1 text-[10px] text-primary-600 dark:text-primary-400">
                                    <CheckCircle className="w-3 h-3" /> System mapped
                                </span>
                            )}
                            {!mapping.IsSystemMapped && mapping.IsEnabled && mapping.HubSpotProperty && propertyByLower.has(mapping.HubSpotProperty.toLowerCase()) && !mapping.IsCustom && (
                                <span className="inline-flex items-center gap-1 text-[10px] text-amber-600 dark:text-amber-400">
                                    <Lightbulb className="w-3 h-3" /> Suggested
                                </span>
                            )}
                            {mapping.IsCustom && (
                                <button
                                    type="button"
                                    onClick={() => removeMapping(index)}
                                    className="p-1 rounded hover:bg-red-50 dark:hover:bg-red-900/20 text-slate-400 hover:text-red-500 dark:hover:text-red-400 transition-colors"
                                    aria-label={`Remove custom field ${mapping.DisplayLabel || mapping.OrbitalXField}`}
                                >
                                    <Trash2 className="w-3.5 h-3.5" />
                                </button>
                            )}
                        </div>
                    </div>
                ))}

                {/* Add custom field inline row */}
                {showAddRow && (
                    <div className="grid grid-cols-12 gap-3 items-center px-3 py-3 rounded-lg border-2 border-dashed border-primary-300 dark:border-primary-700 bg-primary-50/50 dark:bg-primary-900/10">
                        <div className="col-span-1 flex justify-center">
                            <ToggleRow checked={true} onChange={() => {}} ariaLabel="New custom field enabled" size="sm" disabled />
                        </div>
                        <div className="col-span-4">
                            <TextBox
                                name="custom-field-label"
                                value={newLabel}
                                onChange={setNewLabel}
                                placeholder="Field name (e.g. Department)"
                            />
                        </div>
                        <div className="col-span-5">
                            <Select
                                key={`custom-field-hubspot-prop-${newHubSpotProp || 'none'}`}
                                name="custom-field-hubspot-prop"
                                options={propertyOptions}
                                placeholder="Select HubSpot property..."
                                defaultValue={newHubSpotProp}
                                onChange={(e: Event) => setNewHubSpotProp((e.target as HTMLSelectElement).value)}
                            />
                        </div>
                        <div className="col-span-2 flex items-center justify-end gap-1">
                            <button
                                type="button"
                                onClick={addCustomField}
                                disabled={!newLabel.trim()}
                                className="p-1.5 rounded-md bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors md:px-2.5 md:py-1.5"
                                aria-label="Add custom field"
                            >
                                <Check className="w-4 h-4 md:hidden" />
                                <span className="hidden md:inline text-xs font-medium">Add</span>
                            </button>
                            <button
                                type="button"
                                onClick={() => { setShowAddRow(false); setNewLabel(''); setNewHubSpotProp('') }}
                                className="p-1.5 rounded-md text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 transition-colors md:px-2 md:py-1.5"
                                aria-label="Cancel"
                            >
                                <X className="w-4 h-4 md:hidden" />
                                <span className="hidden md:inline text-xs">Cancel</span>
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* Add custom field button */}
            {!showAddRow && (
                <button
                    type="button"
                    onClick={() => setShowAddRow(true)}
                    className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg border-2 border-dashed border-slate-200 dark:border-slate-700 text-sm text-slate-500 dark:text-slate-400 hover:border-primary-300 dark:hover:border-primary-700 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                >
                    <Plus className="w-4 h-4" />
                    Add Custom Field
                </button>
            )}
        </div>
    )
}
